import React, { useState, useEffect } from 'react';
import { ShieldCheck, Clock as ClockIcon, CheckCircle2, ArrowLeftToLine, ShieldOff, PenLine } from 'lucide-react';
import { startPoll } from '../../lib/poll';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ACK_KEY_PREFIX = 'carloo_no_gate_ack_';  // localStorage key per movement id

export default function GateStatusBanner() {
  const [movement, setMovement] = useState(null);
  const [acked, setAcked] = useState(false);

  const fetchActive = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/movements/my-active`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!r.ok) return;
      const d = await r.json();
      const m = d && d.id ? d : null;
      setMovement(m);
      if (m && m.id) {
        setAcked(!!localStorage.getItem(ACK_KEY_PREFIX + m.id));
      } else {
        setAcked(false);
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => startPoll(fetchActive, 45_000), []);  // v2.6.4: was 5 s

  if (!movement) return null;

  const gateOff = movement.gate_control_enabled === false;

  // ---- No-gate-control mode: show "Write on register & Sign" acknowledgement first.
  if (gateOff && !acked) {
    const refLabel = movement.type === 'visit'
      ? 'Visit ID'
      : movement.type === 'break'
        ? `Break ID (${movement.ref_meta?.break_type || 'break'})`
        : 'Movement ID';
    return (
      <div className="rounded-xl p-4 border-2 bg-amber-50 border-amber-300" data-testid="no-gate-ack-banner">
        <div className="flex items-start space-x-3">
          <ShieldOff className="text-amber-700 mt-0.5" size={22} />
          <div className="flex-1">
            <h3 className="font-bold text-amber-900">No gate security — write on register</h3>
            <p className="text-xs text-amber-800 mb-2">Please write this ID on the gate register and sign before leaving.</p>
            <div className="bg-white rounded-lg p-3 text-center border border-dashed border-amber-400 mb-3">
              <p className="text-xs text-gray-500 mb-1">{refLabel}</p>
              <p className="text-3xl font-mono font-bold tracking-widest text-amber-700" data-testid="no-gate-code">
                {movement.short_code}
              </p>
              {movement.ref_meta?.supplier_name && (
                <p className="text-xs text-gray-500 mt-1">→ {movement.ref_meta.supplier_name}</p>
              )}
            </div>
            <button
              onClick={() => {
                localStorage.setItem(ACK_KEY_PREFIX + movement.id, '1');
                setAcked(true);
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-sm"
              data-testid="no-gate-ack-btn"
            >
              <PenLine size={16} />
              OK — I’ve written on the register and signed
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Gate-control ON (original flow): show gate approval states.
  const cfgs = {
    pending_out: {
      bg: 'bg-orange-50 border-orange-300',
      icon: <ClockIcon className="text-orange-600 animate-pulse" size={22} />,
      title: 'Waiting at Gate',
      msg: 'Show this code to the gate security.'
    },
    out_approved: {
      bg: 'bg-green-50 border-green-300',
      icon: <CheckCircle2 className="text-green-600" size={22} />,
      title: 'Gate Approved — You may go out',
      msg: 'Save this code. Show it when you return.'
    },
    pending_in: {
      bg: 'bg-blue-50 border-blue-300',
      icon: <ArrowLeftToLine className="text-blue-600" size={22} />,
      title: 'At Gate to Enter',
      msg: 'Tell this code to gate security to allow you in.'
    }
  };

  const cfg = cfgs[movement.status];
  if (!cfg) return null;

  return (
    <div className={`rounded-xl p-4 border-2 ${cfg.bg}`} data-testid="gate-status-banner">
      <div className="flex items-start space-x-3">
        <ShieldCheck className="text-gray-700 mt-0.5" size={20} />
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            {cfg.icon}
            <h3 className="font-bold">{cfg.title}</h3>
          </div>
          <p className="text-xs text-gray-600 mb-2">{cfg.msg}</p>
          <div className="bg-white rounded-lg p-3 text-center border border-dashed border-gray-300">
            <p className="text-xs text-gray-500 mb-1">Your Gate Code</p>
            <p className="text-3xl font-mono font-bold tracking-widest text-amber-700" data-testid="gate-status-code">
              {movement.short_code}
            </p>
            <p className="text-xs text-gray-500 mt-1 capitalize">
              {movement.type}{movement.ref_meta?.break_type ? `: ${movement.ref_meta.break_type}` : ''}
              {movement.ref_meta?.supplier_name && ` → ${movement.ref_meta.supplier_name}`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
